import React from 'react';
import { X, BookOpen, Mic, Video, Users, BadgePercent, Globe, Megaphone, Handshake, Sparkles, ExternalLink, Star, Tv, PenTool, Camera, CreditCard, QrCode, Link2, Ticket } from 'lucide-react';

const BENEFITS = [
  {
    icon: BookOpen,
    color: 'from-cyan-500 to-blue-600',
    title: 'Acceso a la Colección Literaria y Audiovisual',
    description: 'Accede al catálogo de ViubuxConnect de manera ilimitada. Lee, escucha y aprende de ebooks, podcasts, audiolibros, audiocursos, meditaciones guiadas y más. Forma parte de una economía colaborativa de autores donde ganas simplemente por compartir tu obra.',
  },
  {
    icon: Video,
    color: 'from-purple-500 to-indigo-600',
    title: 'Patrocinio de Evento (1 vez al año)',
    description: 'ViubuxConnect patrocina audio, video y foro para tu evento anual. Organiza una presentación, conferencia, taller o curso. Producido y grabado por Viubux Media. Invita a la comunidad VC con asistencia presencial o por Zoom.',
    extra: 'Genera ganancias: Vende tickets, libros y merchandising libre de comisión.'
  },
  {
    icon: BadgePercent,
    color: 'from-green-500 to-emerald-600',
    title: '15% de Descuento en Viubux Media',
    description: 'Descuento en todos los servicios de producción literaria y audiovisual:',
    list: ['Producción de Audiolibros y Videolibros', 'Booktrailers y Podcast', 'Servicios de Edición y Diseño', 'Photoshoot Profesional', 'NFC Cards', 'Y mucho más']
  },
  {
    icon: Globe,
    color: 'from-blue-500 to-cyan-600',
    title: 'Distribución de hasta 5 Obras',
    description: 'Distribuye hasta 5 obras terminadas dentro de la plataforma ViubuxConnect y alcanza miles de audio-lectores suscritos. Valor de $3,485 MXN incluido con tu membresía.',
  },
  {
    icon: Users,
    color: 'from-amber-500 to-orange-600',
    title: 'Comunidad y Networking Exclusivo',
    description: 'Conecta directamente con autores, creadores y emprendedores para colaborar, aprender y crecer juntos. Organiza clubes de lectura virtuales y sesiones de preguntas en vivo con descuento exclusivo.',
  },
  {
    icon: Handshake,
    color: 'from-pink-500 to-rose-600',
    title: 'Beneficios con Socios Estratégicos',
    description: 'Mejora tus habilidades con descuentos exclusivos en cursos, servicios y mentorías de nuestros socios. Amplía tu red de contactos con profesionales y expertos de diferentes industrias.',
  },
  {
    icon: CreditCard,
    color: 'from-yellow-500 to-amber-600',
    title: 'Comisiones por Referidos',
    description: 'Monetiza por cada referido que consuma contenido de la comunidad VC mes con mes:',
    list: ['25% Bono Rápido por cada suscripción', 'Porcentaje prorrateado de un Pool de Autores del 42%', 'Enlace de afiliado personalizado con tu nombre']
  },
  {
    icon: Tv,
    color: 'from-indigo-500 to-violet-600',
    title: 'Tu Propio Canal de Contenido',
    description: 'Crea tu canal dentro de Viubux.com para monetizar tu contenido y reforzar tu marca personal. Sube audiolibros, videolibros, ebooks, conferencias, podcast y cursos.',
    extra: 'Cobra por suscripción, pago por visión (PPV) o modelo híbrido. Accede a métricas transparentes de tu canal.'
  },
  {
    icon: Sparkles,
    color: 'from-teal-500 to-cyan-600',
    title: 'Acceso a Co-Producciones',
    description: 'Oportunidad de que un co-inversionista aporte hasta un 50% de descuento en la producción de tu audiolibro.',
  },
  {
    icon: Megaphone,
    color: 'from-red-500 to-pink-600',
    title: 'Pro-Marketing',
    description: 'Herramientas profesionales para promover tu obra y marca personal:',
    list: ['Creación de Enlace Único (tipo LinkTree)', 'QR personalizado descargable', 'Booktrailer automatizado con IA', 'Separadores de libros con QR', 'Tarjetas de presentación profesionales']
  },
];

export function ViubuxConnectInfoModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto" data-testid="viubux-connect-info-modal">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative w-full max-w-3xl my-8 bg-gradient-to-b from-[#0a0e2a] to-[#0f133a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-[#00c2ff]/20 via-[#003fdd]/20 to-[#00c2ff]/10" />
          <div className="relative px-6 py-8 text-center">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="btn-close-connect-info"
            >
              <X className="w-4 h-4 text-white" />
            </button>
            <div className="inline-flex items-center gap-2 bg-[#00c2ff]/10 border border-[#00c2ff]/30 rounded-full px-4 py-1 mb-4">
              <Star className="w-4 h-4 text-[#00c2ff]" />
              <span className="text-[#00c2ff] text-sm font-medium">Programa de Afiliados</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Viubux Connect
            </h2>
            <p className="text-[#00c2ff] font-medium text-sm tracking-widest uppercase">
              Conectamos Mentes Brillantes
            </p>
            <p className="text-slate-400 text-sm mt-3 max-w-lg mx-auto">
              Expande tu alcance, diversifica tus ingresos y forma parte de la comunidad de autores, creadores y emprendedores más activa del mundo editorial.
            </p>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="px-6 py-6 space-y-4">
          <h3 className="text-white font-bold text-lg mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            10 Beneficios de tu membresía
          </h3>
          
          {BENEFITS.map((benefit, idx) => {
            const Icon = benefit.icon;
            return (
              <div
                key={idx}
                className="flex gap-4 p-4 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 rounded-xl transition-colors"
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${benefit.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-semibold text-sm mb-1">
                    <span className="text-[#00c2ff] mr-2">{idx + 1}.</span>
                    {benefit.title}
                  </h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                  {benefit.list && (
                    <ul className="mt-2 space-y-1">
                      {benefit.list.map((item, i) => (
                        <li key={i} className="text-slate-300 text-xs flex items-start gap-2">
                          <span className="text-[#00c2ff] mt-0.5">&#8226;</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                  {benefit.extra && (
                    <p className="text-green-400/80 text-xs mt-2 italic">
                      {benefit.extra}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pricing Section */}
        <div className="px-6 py-6 border-t border-white/10">
          <h3 className="text-white font-bold text-lg mb-4 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Planes de Membresía
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="https://Connect.Viubux.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 bg-white/[0.03] border border-white/10 hover:border-white/30 hover:bg-white/[0.06] rounded-xl text-center transition-all cursor-pointer group"
              data-testid="plan-mensual-link"
            >
              <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Plan Mensual</p>
              <p className="text-white text-sm mb-1">Inscripción: <span className="font-bold">$4,997</span></p>
              <p className="text-slate-400 text-xs">+ 12 pagos de $199</p>
              <p className="text-white font-bold text-lg mt-2">$7,385 <span className="text-xs font-normal text-slate-500">MXN total</span></p>
              <span className="inline-flex items-center gap-1 mt-3 text-[#00c2ff] text-xs font-medium group-hover:underline">
                Inscribirme <ExternalLink className="w-3 h-3" />
              </span>
            </a>
            <a
              href="https://buy.stripe.com/eVqdR9daW3a83t1aHV2wU1p"
              target="_blank"
              rel="noopener noreferrer"
              className="block p-5 bg-gradient-to-br from-[#00c2ff]/10 to-[#003fdd]/10 border border-[#00c2ff]/30 hover:border-[#00c2ff]/60 hover:from-[#00c2ff]/15 hover:to-[#003fdd]/15 rounded-xl text-center relative transition-all cursor-pointer group"
              data-testid="plan-anual-link"
            >
              <div className="absolute -top-2 right-3 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                -20%
              </div>
              <p className="text-[#00c2ff] text-xs uppercase tracking-wider mb-2">Plan Anual</p>
              <p className="text-white text-sm mb-1">Inscripción + 1 año de acceso</p>
              <p className="text-white font-bold text-lg mt-2">$5,908 <span className="text-xs font-normal text-slate-400">MXN total</span></p>
              <p className="text-green-400 text-xs mt-1 font-medium">Recomendado</p>
              <span className="inline-flex items-center gap-1 mt-2 text-[#00c2ff] text-xs font-medium group-hover:underline">
                Inscribirme <ExternalLink className="w-3 h-3" />
              </span>
            </a>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="px-6 py-5 border-t border-white/10 bg-white/[0.02] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-slate-400 text-xs text-center sm:text-left">
            ¿Preguntas? <a href="mailto:connect@viubux.com" className="text-[#00c2ff] hover:underline">connect@viubux.com</a> | <a href="tel:+525548564696" className="text-[#00c2ff] hover:underline">+52 55 4856 4696</a>
          </p>
          <a
            href="https://Connect.Viubux.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-[#00c2ff] to-[#003fdd] text-white font-semibold px-6 py-2.5 rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            Visitar Connect.Viubux.com
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}

export default ViubuxConnectInfoModal;
